import { useEffect, useState, type CSSProperties, type FormEvent, type MouseEvent } from "react";

import heroBulbImage from "./assets/edison/hero-bulb.png";
import heroBulbWideImage from "./assets/edison/hero-bulb-wide.jpg";
import truckImage from "./assets/edison/field-lab.png";
import workDashboardImage from "./assets/edison/work-dashboard.png";
import workPhoneImage from "./assets/edison/work-phone.png";
import workTabletImage from "./assets/edison/work-tablet.png";
import evidencePacketImage from "./assets/work/evidence-packet-builder.png";
import fieldSignalImage from "./assets/work/field-signal-monitor.png";
import inspectionLensImage from "./assets/work/inspection-lens.png";
import tenderTrailImage from "./assets/work/tendertrail.png";
import videoEvidenceImage from "./assets/work/video-evidence-desk.png";
import voiceBriefImage from "./assets/work/voice-brief-room.png";

const navItems = [
  { label: "About", href: "/#about", section: "about" },
  { label: "Solutions", href: "/#solutions", section: "solutions" },
  { label: "Work", href: "/work", path: "/work" },
  { label: "Lab Notes", href: "/lab-notes", path: "/lab-notes" },
  { label: "Careers", href: "/careers", path: "/careers" }
];

const pageContent = {
  "/lab-notes": {
    eyebrow: "Lab Notes",
    title: "Useful notes, not noise.",
    body:
      "Short public notes from the parts of the work that can be shared: method writeups, product observations, release notes, and decisions worth preserving.",
    cards: [
      {
        title: "Build Notes",
        body: "Implementation notes from prototypes, dashboards, and product surfaces."
      },
      {
        title: "Method Notes",
        body: "How we test claims, gather evidence, and keep proofs bounded."
      },
      {
        title: "Release Notes",
        body: "Small changelogs for public demos and shipped site surfaces."
      },
      {
        title: "Field Observations",
        body: "Practical notes from messy workflows, real data, and user feedback."
      }
    ]
  },
  "/careers": {
    eyebrow: "Careers",
    title: "Small team. Serious builders.",
    body:
      "Edison Internet Labs is built around people who can turn ambiguous problems into working systems. The team stays small, practical, and close to the work.",
    cards: [
      {
        title: "Product Engineering",
        body: "Full-stack builders who can ship interfaces, data flows, and production-grade details."
      },
      {
        title: "Applied AI",
        body: "Researchers and operators who care about real evaluation, not speculative demos."
      },
      {
        title: "Design Systems",
        body: "Designers who can make complex tools feel composed, legible, and durable."
      },
      {
        title: "Operations",
        body: "People who make handoffs, launches, and client delivery more reliable."
      }
    ]
  }
} as const;

type InfoPath = keyof typeof pageContent;
type PagePath = InfoPath | "/work" | "/";

const workProjects = [
  {
    title: "Video Evidence Desk",
    kicker: "Video, motion, keyframes",
    image: videoEvidenceImage,
    pin: "teal",
    tilt: "-2.2deg",
    body:
      "Turns long video or live camera feeds into compact review packets: keyframes, motion traces, scene-change notes, and human-readable evidence summaries.",
    tags: ["video", "vision", "temporal metadata"]
  },
  {
    title: "TenderTrail",
    kicker: "Procurement, documents, risk",
    image: tenderTrailImage,
    pin: "gold",
    tilt: "1.6deg",
    body:
      "Helps teams read bid packages, map requirements to source pages, flag compliance gaps, and decide whether a pursuit is ready to move.",
    tags: ["documents", "compliance", "decision support"]
  },
  {
    title: "Field Signal Monitor",
    kicker: "Sensors, telemetry, anomalies",
    image: fieldSignalImage,
    pin: "blue",
    tilt: "-1.1deg",
    body:
      "Watches live field streams, groups unusual event bursts, and gives operators a fast trail from raw telemetry to a reviewable incident note.",
    tags: ["sensors", "streaming", "operations"]
  },
  {
    title: "Voice Brief Room",
    kicker: "Audio, transcripts, action memory",
    image: voiceBriefImage,
    pin: "coral",
    tilt: "2.1deg",
    body:
      "Converts meetings and calls into decisions, open questions, owners, and follow-up tasks without pretending the transcript is the whole truth.",
    tags: ["audio", "language", "workflow"]
  },
  {
    title: "Inspection Lens",
    kicker: "Images, QA, field reports",
    image: inspectionLensImage,
    pin: "lime",
    tilt: "-1.8deg",
    body:
      "Reviews inspection photos, compares field conditions, and prepares a cleaner queue of defects, passes, and items needing a second look.",
    tags: ["images", "quality review", "field work"]
  },
  {
    title: "Evidence Packet Builder",
    kicker: "Research, citations, delivery",
    image: evidencePacketImage,
    pin: "violet",
    tilt: "1.2deg",
    body:
      "Assembles source-backed client packets from notes, images, documents, and tables while keeping claims tied to the material that supports them.",
    tags: ["research", "citations", "handoff"]
  }
] as const;

function normalizePath(pathname: string): PagePath {
  if (pathname === "/work" || pathname === "/lab-notes" || pathname === "/careers") return pathname;
  return "/";
}

function BulbMark() {
  return (
    <svg className="brand-icon" viewBox="0 0 38 48" fill="none" aria-hidden="true">
      <ellipse cx="19" cy="20" rx="13" ry="17" />
      <path d="M19 8 C13 8 11 14 13 19 C14.6 23 14 25 13.5 28 M19 8 C25 8 27 14 25 19 C23.4 23 24 25 24.5 28" />
      <path d="M13 30 H25 M13.8 33 H24.2 M15 36 H23" />
      <rect x="14.5" y="38.5" width="9" height="6" rx="1.4" />
    </svg>
  );
}

function ArrowLine({ dark = false }: { dark?: boolean }) {
  return (
    <svg className={dark ? "arrow-line arrow-line--dark" : "arrow-line"} viewBox="0 0 34 10" fill="none" aria-hidden="true">
      <line x1="0" y1="5" x2="30" y2="5" />
      <polyline points="25,1 31,5 25,9" />
    </svg>
  );
}

function App() {
  const [currentPath, setCurrentPath] = useState<PagePath>(() => normalizePath(window.location.pathname));
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  useEffect(() => {
    const onPopState = () => setCurrentPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (currentPath === "/" && window.location.hash) {
      const id = window.location.hash.slice(1);
      window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
    }
  }, [currentPath]);

  function navigate(path: PagePath) {
    window.history.pushState({}, "", path);
    setCurrentPath(path);
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goHomeSection(event: MouseEvent<HTMLAnchorElement>, section: string) {
    event.preventDefault();
    setMenuOpen(false);
    const targetUrl = `/#${section}`;

    if (currentPath !== "/") {
      window.history.pushState({}, "", targetUrl);
      setCurrentPath("/");
      window.setTimeout(() => document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      return;
    }

    window.history.pushState({}, "", `#${section}`);
    document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function goPage(event: MouseEvent<HTMLAnchorElement>, path: PagePath) {
    event.preventDefault();
    navigate(path);
  }

  function openContact(event?: MouseEvent<HTMLAnchorElement>) {
    event?.preventDefault();
    setMenuOpen(false);
    setContactOpen(true);
    setFormStatus("idle");
  }

  async function handleContactSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const note = String(formData.get("note") || "").trim();

    setFormStatus("sending");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, email, note })
      });

      if (!response.ok) throw new Error("Contact request failed");
      form.reset();
      setFormStatus("success");
    } catch {
      setFormStatus("error");
    }
  }

  return (
    <div className="site-shell" id="top">
      <header className="site-header">
        <a className="brand-lockup" href="/" aria-label="Edison Internet Labs home" onClick={(event) => goPage(event, "/")}>
          <BulbMark />
          <span className="brand-text">
            <strong>Edison</strong>
            <small>Internet Labs</small>
          </span>
        </a>

        <button
          className="nav-toggle"
          type="button"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={menuOpen ? "nav-list nav-list--open" : "nav-list"} aria-label="Primary navigation">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(event) => {
                if (item.section) goHomeSection(event, item.section);
                if (item.path) goPage(event, item.path as PagePath);
              }}
            >
              {item.label}
            </a>
          ))}
          <a className="nav-cta" href="#contact" onClick={openContact}>
            Let's Build
          </a>
        </nav>
      </header>

      {currentPath === "/" ? (
        <HomePage goHomeSection={goHomeSection} goPage={goPage} openContact={openContact} />
      ) : currentPath === "/work" ? (
        <WorkPage openContact={openContact} />
      ) : (
        <InternalPage path={currentPath} openContact={openContact} />
      )}

      {contactOpen && (
        <div className="contact-overlay" role="dialog" aria-modal="true" aria-labelledby="contact-title">
          <div className="contact-modal">
            <button className="modal-close" type="button" aria-label="Close contact form" onClick={() => setContactOpen(false)}>
              X
            </button>
            <p className="modal-eyebrow">Start A Conversation</p>
            <h2 id="contact-title">Tell us what you want to build.</h2>
            <form className="modal-form" onSubmit={handleContactSubmit}>
              <label>
                Name
                <input name="name" autoComplete="name" required />
              </label>
              <label>
                Email
                <input name="email" type="email" autoComplete="email" required />
              </label>
              <label>
                What should we test?
                <textarea name="note" required placeholder="A workflow, product idea, automation, or page that needs a prototype." />
              </label>
              <button className="button button--gold" type="submit" disabled={formStatus === "sending"}>
                {formStatus === "sending" ? "Sending" : "Capture Signal"}
              </button>
              {formStatus === "success" && <p className="form-status">Received. We'll review it and reply directly.</p>}
              {formStatus === "error" && <p className="form-status form-status--error">That did not send. Check the email and try again.</p>}
            </form>
          </div>
        </div>
      )}

      <footer className="site-footer">
        <span>(c) 2026 Edison Internet Labs</span>
        <nav aria-label="Footer navigation">
          <a href="/lab-notes" onClick={(event) => goPage(event, "/lab-notes")}>
            Lab Notes
          </a>
          <a href="/careers" onClick={(event) => goPage(event, "/careers")}>
            Careers
          </a>
          <a href="#contact" onClick={openContact}>
            Contact
          </a>
        </nav>
      </footer>
    </div>
  );
}

function HomePage({
  goHomeSection,
  goPage,
  openContact
}: {
  goHomeSection: (event: MouseEvent<HTMLAnchorElement>, section: string) => void;
  goPage: (event: MouseEvent<HTMLAnchorElement>, path: PagePath) => void;
  openContact: (event?: MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <main>
        <section id="about" className="hero-section" style={{ backgroundImage: `url(${heroBulbWideImage})` }}>
          <div className="hero-scrim" />
          <div className="hero-content">
            <h1>
              Brilliant Ideas.
              <br />
              Powerful Connections.
            </h1>
            <div className="script-line">
              <span>Real Results.</span>
              <svg viewBox="0 0 300 16" fill="none" aria-hidden="true">
                <path d="M2 11 C70 4 150 3 220 6 C255 7.5 280 9 298 6" />
              </svg>
            </div>
            <p>
              Edison Internet Labs builds high-performance digital solutions that connect people, simplify
              complex systems, and drive business forward.
            </p>
            <a className="button button--gold" href="/#solutions" onClick={(event) => goHomeSection(event, "solutions")}>
              Let's Build Something
              <ArrowLine dark />
            </a>
          </div>
        </section>

        <section id="solutions" className="solutions-section">
          <div className="solutions-copy">
            <h2>Wired For What's Next</h2>
            <p>We're a full-service digital lab helping companies innovate and scale in a connected world.</p>

            <div className="service-grid">
              <article>
                <svg viewBox="0 0 46 46" fill="none" aria-hidden="true">
                  <circle cx="23" cy="23" r="17" />
                  <ellipse cx="23" cy="23" rx="7" ry="17" />
                  <line x1="6" y1="23" x2="40" y2="23" />
                  <line x1="9.5" y1="14" x2="36.5" y2="14" />
                  <line x1="9.5" y1="32" x2="36.5" y2="32" />
                </svg>
                <h3>Digital Strategy</h3>
                <p>Smart direction. Stronger outcomes.</p>
              </article>
              <article>
                <svg viewBox="0 0 46 46" fill="none" aria-hidden="true">
                  <circle cx="23" cy="23" r="2.4" />
                  <ellipse cx="23" cy="23" rx="18" ry="7" />
                  <ellipse cx="23" cy="23" rx="18" ry="7" transform="rotate(60 23 23)" />
                  <ellipse cx="23" cy="23" rx="18" ry="7" transform="rotate(120 23 23)" />
                </svg>
                <h3>Custom Solutions</h3>
                <p>Built for your business. Designed to perform.</p>
              </article>
              <article>
                <svg viewBox="0 0 46 46" fill="none" aria-hidden="true">
                  <circle cx="23" cy="12" r="2.3" />
                  <path d="M23 13.5 L16.5 38 M23 13.5 L29.5 38 M18.6 29 H27.4" />
                  <path d="M15 9 q-4.5 7 0 14" />
                  <path d="M11 5.5 q-7.5 11.5 0 23" />
                  <path d="M31 9 q4.5 7 0 14" />
                  <path d="M35 5.5 q7.5 11.5 0 23" />
                </svg>
                <h3>Connected Experiences</h3>
                <p>Seamless. Intuitive. Human-centered.</p>
              </article>
              <article>
                <svg viewBox="0 0 46 46" fill="none" aria-hidden="true">
                  <path d="M8 31 A16 16 0 0 1 38 31" />
                  <line x1="23" y1="31" x2="31.5" y2="19.5" />
                  <circle cx="23" cy="31" r="2.4" />
                  <line x1="11" y1="31" x2="13.5" y2="31" />
                  <line x1="35" y1="31" x2="32.5" y2="31" />
                  <line x1="23" y1="17" x2="23" y2="19.5" />
                </svg>
                <h3>Performance At Scale</h3>
                <p>Fast. Reliable. Future-ready.</p>
              </article>
            </div>

            <a className="text-link" href="/work" onClick={(event) => goPage(event, "/work")}>
              Explore Our Solutions
              <ArrowLine />
            </a>
          </div>

          <div className="solutions-photo" style={{ backgroundImage: `url(${truckImage})` }} aria-hidden="true" />
        </section>

        <section id="work" className="work-section">
          <div className="work-wrap">
            <div className="work-copy">
              <h2>Ideas In Action</h2>
              <div className="divider">
                <span />
                <svg viewBox="0 0 20 26" aria-hidden="true">
                  <path d="M11 0 L0 15 H8 L7 26 L20 9 H11 Z" />
                </svg>
                <span />
              </div>
              <p>
                Solutions we've built.
                <br />
                Impact we're proud of.
              </p>
              <a className="text-link text-link--dark" href="/work" onClick={(event) => goPage(event, "/work")}>
                See Our Work
                <ArrowLine dark />
              </a>
            </div>

            <div className="work-cards">
              <article style={{ backgroundImage: `url(${workTabletImage})` }}>
                <span>Platform Modernization</span>
              </article>
              <article style={{ backgroundImage: `url(${workPhoneImage})` }}>
                <span>Mobile Experiences</span>
              </article>
              <article style={{ backgroundImage: `url(${workDashboardImage})` }}>
                <span>Data &amp; Integration</span>
              </article>
            </div>
          </div>
        </section>

        <section id="contact" className="cta-section">
          <div className="cta-image" style={{ backgroundImage: `url(${heroBulbImage})` }} aria-hidden="true" />
          <div className="cta-wrap">
            <div className="cta-copy">
              <h2>Let's Build What's Possible.</h2>
              <div className="quote-mark">
                <ArrowLine />
                <BulbMark />
                <ArrowLine />
              </div>
              <p>Great ideas deserve the right team behind them.</p>
              <a className="button button--gold" href="#contact" onClick={openContact}>
                Start A Conversation
              </a>
            </div>

            <aside className="quote-card">
              <p>"The value of an idea lies in the using of it."</p>
              <strong>- Thomas Edison</strong>
              <span>Thos. A. Edison</span>
            </aside>
          </div>
        </section>
    </main>
  );
}

function WorkPage({ openContact }: { openContact: (event?: MouseEvent<HTMLAnchorElement>) => void }) {
  return (
    <main className="work-page">
      <section className="work-page-hero">
        <p>Selected Work</p>
        <h1>Real-world problems, multimodal AI systems.</h1>
        <span>
          A public board of Edison work in progress: video, documents, audio, images, sensor feeds, and source-backed delivery tools. The point is not a glossy demo. The point is a working surface for a real decision.
        </span>
        <a className="button button--gold" href="#contact" onClick={openContact}>
          Start A Conversation
        </a>
      </section>

      <section className="pinboard-section" aria-label="Selected Edison AI work">
        <div className="pinboard">
          {workProjects.map((project, index) => (
            <article
              className="polaroid-card"
              key={project.title}
              style={{ "--tilt": project.tilt } as CSSProperties}
            >
              <span className={`thumbtack thumbtack--${project.pin}`} aria-hidden="true" />
              <figure>
                <img src={project.image} alt={`${project.title} application screenshot`} />
                <figcaption>
                  <small>{project.kicker}</small>
                  <strong>
                    {String(index + 1).padStart(2, "0")} / {project.title}
                  </strong>
                </figcaption>
              </figure>
              <p>{project.body}</p>
              <ul aria-label={`${project.title} modalities`}>
                {project.tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function InternalPage({ path, openContact }: { path: InfoPath; openContact: (event?: MouseEvent<HTMLAnchorElement>) => void }) {
  const page = pageContent[path];

  return (
    <main className="internal-page">
      <section className="internal-hero">
        <p>{page.eyebrow}</p>
        <h1>{page.title}</h1>
        <span>{page.body}</span>
        <a className="button button--gold" href="#contact" onClick={openContact}>
          Start A Conversation
        </a>
      </section>
      <section className="internal-card-grid" aria-label={`${page.eyebrow} areas`}>
        {page.cards.map((card) => (
          <article key={card.title} className={"image" in card ? "internal-card internal-card--image" : "internal-card"}>
            {"image" in card && <div className="internal-card-photo" style={{ backgroundImage: `url(${card.image})` }} aria-hidden="true" />}
            <h2>{card.title}</h2>
            <p>{card.body}</p>
          </article>
        ))}
      </section>
    </main>
  );
}

export default App;
